import { useMemo, useState } from "react"
import { RIGA_PRICE_GROUP } from "../../api/config"
import {
	attributeWrites,
	groupWrites,
	productWrites,
} from "../../api/mutations"
import { useAttributes, useGroupRows } from "../../api/queries"
import type { ProductGroup, ProductRow } from "../../api/types"
import type { GroupRights } from "../../auth/rights"
import { ActionNotes, Note } from "../../components/Note"
import { useConfirm } from "../../hooks/useConfirm"
import AddColumn from "./AddColumn"
import {
	availableAttributes,
	columnsOf,
	previewSiteName,
	valueOf,
	type Column,
} from "./columns"
import ImportancePanel from "./ImportancePanel"
import ProductPicker from "./ProductPicker"
import SiteNameHelp from "./SiteNameHelp"
import { useWriter } from "./useWriter"

/**
 * Вкладка «Табличная часть»: товары карточки и колонки-атрибуты.
 *
 * Колонками управляют прямо в шапке таблицы: заголовок перетаскивается на новое
 * место, крестик убирает колонку, пустая ячейка справа заводит новую. Приоритет
 * важных атрибутов — редкая настройка, она вынесена в отдельную панель.
 *
 * Значения атрибутов копятся в локальном черновике и уходят одним запросом —
 * так же, как в ECM. Состав товаров и колонок сохраняется сразу: пакетного
 * метода для них на бэке нет.
 */
export default function TablePart({
	group,
	languageId,
	rights,
}: {
	group: ProductGroup
	languageId: number
	rights: GroupRights
}) {
	const rows = useGroupRows(group.id, languageId)
	const attributes = useAttributes(languageId)
	const writer = useWriter(group.id)
	const { ask, dialog } = useConfirm()

	const [edits, setEdits] = useState<Record<string, string>>({})
	const [replacing, setReplacing] = useState<string | null>(null)
	const [showImportance, setShowImportance] = useState(false)
	const [showNameHelp, setShowNameHelp] = useState(false)
	const [dragId, setDragId] = useState<number | null>(null)
	const [overId, setOverId] = useState<number | null>(null)

	const columns = useMemo(
		() => columnsOf(group, attributes.data),
		[group, attributes.data]
	)
	const available = useMemo(
		() => availableAttributes(group, attributes.data),
		[group, attributes.data]
	)

	const list = rows.data ?? []
	const dirty = Object.entries(edits)

	// у набора один товар — сам набор; состав компонентов правится ниже,
	// отдельной панелью «Состав набора» (ToolsetComposition.tsx)
	const isToolsetFull = group.isToolset && list.length > 0

	const save = async () => {
		if (dirty.length === 0) return
		const ok = await writer.run(() =>
			productWrites.saveValues(
				group.id,
				dirty.map(([cell, value]) => {
					const [productId, attributeId] = cell.split(":")
					return { productId, attributeId: Number(attributeId), value }
				})
			)
		)
		if (ok) setEdits({})
	}

	const replaceProduct = (newProductId: string) => {
		const productId = replacing
		if (!productId) return
		setReplacing(null)
		if (productId === newProductId) return
		void writer.run(() =>
			productWrites.replace(group.id, productId, newProductId)
		)
	}

	const moveProduct = (productId: string, from: number, to: number) =>
		void writer.run(() => productWrites.move(group.id, productId, from, to))

	/**
	 * Перенос колонки на место другой.
	 * Бэк умеет только меняться местами с одним атрибутом, поэтому перенос —
	 * цепочка обменов с соседями: 1-2 запроса на обычный сдвиг, N на перенос
	 * через всю таблицу.
	 */
	const reorder = (fromId: number, toId: number) => {
		const from = columns.findIndex(column => column.id === fromId)
		const to = columns.findIndex(column => column.id === toId)
		if (from < 0 || to < 0 || from === to) return
		const step = to > from ? 1 : -1
		void writer.run(async () => {
			for (let at = from; at !== to; at += step)
				await attributeWrites.swap(group.id, fromId, columns[at + step].id)
		})
	}

	const removeColumn = async (column: Column) => {
		const yes = await ask({
			title: `Убрать колонку «${column.name}»?`,
			text: "Значения этого атрибута у всех товаров карточки будут удалены.",
			confirm: "Убрать",
			danger: true,
		})
		if (!yes) return
		void writer.run(() => attributeWrites.remove(group.id, column.id))
	}

	const importantColumns = columns
		.filter(column => column.isImportant)
		.sort((a, b) => (a.importantSort ?? 0) - (b.importantSort ?? 0))

	/** Приоритет важного атрибута: место 1 закреплено за главным, остальные ходят по 2..N. */
	const moveImportant = (column: Column, step: number) => {
		if (column.importantSort === null) return
		const targetSort = column.importantSort + step
		if (targetSort < 2 || targetSort > importantColumns.length) return
		const target = columns.find(item => item.importantSort === targetSort)
		if (!target) return
		void writer.run(() =>
			attributeWrites.swapImportant(
				group.id,
				column.id,
				target.id,
				column.importantSort!,
				targetSort
			)
		)
	}

	const setMain = (column: Column) =>
		void writer.run(() =>
			groupWrites.update({ ...group, mainAttributeId: column.id }, languageId)
		)

	const dragIndex = columns.findIndex(column => column.id === dragId)
	// колонки таблицы: № + код + наименование + имя на сайте + атрибуты + «новая» + действия
	const columnCount = columns.length + (rights.columns ? 6 : 5)
	const hasSellmark = !!group.sellmarkId
	/** Клиентское превью колонки «Имя на сайте» — подробности в `columns.ts`. */
	const siteNameOf = (row: ProductRow) =>
		previewSiteName(group.name, importantColumns, row, hasSellmark)

	return (
		<>
			<ActionNotes action={writer} />

			{rights.addProduct && !rights.columns && (
				<Note tone="hint">
					У карточки ещё нет прайс-группы — она появится вместе с первым
					товаром. До этого колонки-атрибуты ERP набирать не даёт.
				</Note>
			)}

			<div className="tp-bar">
				<span className="tp-bar__count">
					{list.length} {group.isToolset ? "" : "поз."}
				</span>
				{rights.columns && columns.length > 1 && (
					<span className="muted hint-inline">
						колонки переставляются перетаскиванием заголовка
					</span>
				)}
				<span className="tp-bar__spacer" />
				{dirty.length > 0 && (
					<span className="tp-dirty">не сохранено: {dirty.length}</span>
				)}
				<button
					className="btn btn--primary"
					disabled={dirty.length === 0 || !rights.values || writer.busy}
					title="Сохранить значения атрибутов (Ctrl+S)"
					onClick={() => void save()}
				>
					Сохранить
				</button>
				{importantColumns.length > 0 && (
					<button
						className="btn"
						aria-pressed={showImportance}
						title="Порядок важных атрибутов и главный атрибут карточки"
						onClick={() => setShowImportance(value => !value)}
					>
						Важные · {importantColumns.length}
					</button>
				)}
				<button
					className="btn"
					aria-pressed={showNameHelp}
					title="Как ERP собирает имя товара для сайта, и кнопка пересборки по всей базе"
					onClick={() => setShowNameHelp(value => !value)}
				>
					Имя на сайте
				</button>
			</div>

			{showImportance && (
				<ImportancePanel
					columns={importantColumns}
					rows={list}
					groupName={group.name}
					hasSellmark={hasSellmark}
					rights={rights}
					busy={writer.busy}
					onMove={moveImportant}
					onSetMain={setMain}
				/>
			)}

			{showNameHelp && <SiteNameHelp />}

			{rights.addProduct && isToolsetFull && !replacing && (
				<Note tone="hint">
					В наборе только один товар — сам набор. Заменить его можно кнопкой ⇄
					в строке, а состав компонентов правится ниже, в панели «Состав набора».
				</Note>
			)}

			{((rights.addProduct && !isToolsetFull) || replacing) && (
				<ProductPicker
					languageId={languageId}
					priceGroupId={list.length > 0 ? group.priceGroupId : RIGA_PRICE_GROUP}
					busy={writer.busy}
					taken={list.map(row => row.id)}
					replacing={replacing}
					onCancelReplace={() => setReplacing(null)}
					onPick={id =>
						replacing
							? replaceProduct(id)
							: void writer.run(() => productWrites.add(group.id, [id]))
					}
				/>
			)}

			<div className="panel tp-tablewrap">
				<table
					className="products tp-table"
					onKeyDown={event => {
						// code, а не key: на русской раскладке key даёт «ы»
						if ((event.ctrlKey || event.metaKey) && event.code === "KeyS") {
							event.preventDefault()
							void save()
						}
					}}
				>
					<thead>
						<tr>
							<th className="tp-col-sort">№</th>
							<th className="tp-col-code">Код</th>
							{/* забирает всю свободную ширину: колонки-атрибуты ужимаются
							    до ширины своего заголовка, остальное достаётся имени */}
							<th className="tp-name">Наименование</th>
							<th
								className="tp-sitename"
								title="Клиентское превью: как ERP соберёт имя товара для сайта из важных атрибутов. Кнопка «Имя на сайте» выше объясняет механику и запускает пересборку по всей базе"
							>
								Имя на сайте
							</th>

							{columns.map((column, index) => (
								<th
									key={column.id}
									className={
										"tp-col" +
										(dragId === column.id ? " tp-col--dragged" : "") +
										(overId === column.id && dragId !== column.id
											? index > dragIndex
												? " tp-col--after"
												: " tp-col--before"
											: "")
									}
									draggable={rights.columns && !writer.busy}
									onDragStart={event => {
										setDragId(column.id)
										event.dataTransfer.effectAllowed = "move"
										// Firefox не начинает перетаскивание без данных
										event.dataTransfer.setData("text/plain", String(column.id))
									}}
									onDragOver={event => {
										if (dragId === null) return
										event.preventDefault()
										setOverId(column.id)
									}}
									onDrop={event => {
										event.preventDefault()
										if (dragId !== null) reorder(dragId, column.id)
										setDragId(null)
										setOverId(null)
									}}
									onDragEnd={() => {
										setDragId(null)
										setOverId(null)
									}}
								>
									<div className="tp-th">
										{rights.columns && (
											<span
												className="tp-grip"
												title="Перетащите, чтобы переставить колонку"
											>
												⠿
											</span>
										)}
										<span className="tp-th__name" title={column.name}>
											{column.name}
										</span>
										<button
											className={
												"tp-starbtn" +
												(column.isImportant ? " tp-starbtn--on" : "")
											}
											disabled={
												!rights.important || column.isMain || writer.busy
											}
											title={
												column.isMain
													? "Главный атрибут карточки: идёт первым в названии товара на сайте. Признак снять нельзя"
													: column.isImportant
														? `Важный атрибут, ${column.importantSort}-й в названии товара на сайте. Нажмите, чтобы убрать из названия`
														: "Сделать важным: значение атрибута попадёт в название товара на сайте"
											}
											onClick={() =>
												void writer.run(() =>
													attributeWrites.toggleImportant(group.id, column.id)
												)
											}
										>
											{column.isImportant
												? column.isMain
													? "★"
													: `★${column.importantSort}`
												: "☆"}
										</button>
										{rights.columns && (
											<button
												className="tp-colx"
												title={`Убрать колонку «${column.name}»`}
												disabled={writer.busy}
												onClick={() => void removeColumn(column)}
											>
												✕
											</button>
										)}
									</div>
								</th>
							))}

							{rights.columns && (
								<th className="tp-addcol">
									<AddColumn
										available={available}
										busy={writer.busy}
										onAdd={id =>
											void writer.run(() =>
												attributeWrites.add(group.id, id)
											)
										}
									/>
								</th>
							)}

							<th className="tp-col-act" />
						</tr>
					</thead>
					<tbody>
						{rows.isLoading && (
							<tr>
								<td colSpan={columnCount}>
									<span className="spinner" />
								</td>
							</tr>
						)}

						{list.map((row, index) => (
							<tr
								key={row.id}
								className={row.isInStatistic ? "" : "tp-row--out"}
							>
								<td>
									<span className="tp-sort">
										<span className="muted mono">{row.sort ?? index + 1}</span>
										{rights.sortProducts && list.length > 1 && (
											<span className="tp-sort__moves">
												<button
													className="tp-mini"
													title="Выше"
													disabled={index === 0 || writer.busy}
													onClick={() =>
														moveProduct(row.id, row.sort ?? index + 1, index)
													}
												>
													↑
												</button>
												<button
													className="tp-mini"
													title="Ниже"
													disabled={index === list.length - 1 || writer.busy}
													onClick={() =>
														moveProduct(row.id, row.sort ?? index + 1, index + 2)
													}
												>
													↓
												</button>
											</span>
										)}
									</span>
								</td>
								<td className="mono tp-code">{row.id}</td>
								<td className="tp-name" title={row.name}>
									{row.name}
									{!row.isInStatistic && (
										<span
											className="tp-flag"
											title="Товар выведен из статистики"
										>
											не в статистике
											{row.stockRus ? " · есть остаток" : ""}
										</span>
									)}
								</td>
								<td className="tp-sitename" title={siteNameOf(row) ?? undefined}>
									{siteNameOf(row) ?? (
										<span
											className="muted"
											title="ERP не соберёт имя: не заполнен хотя бы один важный атрибут"
										>
											—
										</span>
									)}
								</td>
								{columns.map(column => {
									const cell = `${row.id}:${column.id}`
									const stored = valueOf(row, column.id)
									const changed = cell in edits
									return (
										<td
											key={column.id}
											className={
												"tp-val" +
												(dragId === column.id ? " tp-col--dragged" : "")
											}
										>
											<input
												className={
													"tp-cell" + (changed ? " tp-cell--dirty" : "")
												}
												value={changed ? edits[cell] : stored}
												disabled={!rights.values}
												onChange={event => {
													const value = event.target.value
													setEdits(current => {
														const next = { ...current }
														if (value === stored) delete next[cell]
														else next[cell] = value
														return next
													})
												}}
												onKeyDown={event => {
													if (event.key === "Enter") void save()
												}}
											/>
										</td>
									)
								})}
								{rights.columns && <td />}
								<td>
									<span className="tp-actions">
										{rights.replaceProduct && (
											<button
												className="tp-mini"
												title="Заменить товар другим"
												disabled={writer.busy}
												onClick={() => setReplacing(row.id)}
											>
												⇄
											</button>
										)}
										{rights.removeProduct && (
											<button
												className="tp-mini tp-mini--danger"
												title="Убрать товар из карточки"
												disabled={writer.busy}
												onClick={() =>
													void writer.run(() =>
														productWrites.remove(group.id, row.id)
													)
												}
											>
												✕
											</button>
										)}
									</span>
								</td>
							</tr>
						))}

						{!rows.isLoading && list.length === 0 && (
							<tr>
								<td colSpan={columnCount} className="muted">
									В карточке пока нет товаров. Введите артикул или найдите
									товар по названию в строке выше.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{dialog}
		</>
	)
}
